import { z } from 'zod';
import { refersTo } from '../common/refers-to';

// Lifecycle status of a workspace invitation. `pending` is the live state an
// invitee can still accept; the rest are terminal. WorkOS owns the underlying
// token/expiry/accept-once; this mirrors the state for the pending-invitations
// panel. lower_snake matches the product labels (Ausstehend · Angenommen ·
// Widerrufen · Abgelaufen).
export const InvitationStatusSchema = z.enum(['pending', 'accepted', 'revoked', 'expired']);

export type InvitationStatus = z.infer<typeof InvitationStatusSchema>;

export const PendingInvitationSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  // Null for a system/admin-issued invite with no member issuer (e.g. the
  // workspace owner, IMM-271); `issuedByName` is then empty.
  issuedByMemberId: refersTo('member', z.uuid()).nullable(),
  issuedByName: z.string(),
  expiresAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});

export type PendingInvitation = z.infer<typeof PendingInvitationSchema>;

export const ListPendingInvitationsPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const ListPendingInvitationsResponseSchema = z.object({
  data: z.array(PendingInvitationSchema),
});

export type ListPendingInvitationsResponse = z.infer<typeof ListPendingInvitationsResponseSchema>;

export const ResendInvitationPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

// Resend revokes the previous invitation and issues a fresh one under a new id,
// so the response returns the new pending invitation.
export const ResendInvitationResponseSchema = PendingInvitationSchema;

export type ResendInvitationResponse = z.infer<typeof ResendInvitationResponseSchema>;

export const RevokeInvitationPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

export const RevokeInvitationResponseSchema = z.object({ id: z.uuid(), status: InvitationStatusSchema });

export type RevokeInvitationResponse = z.infer<typeof RevokeInvitationResponseSchema>;
